import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  Injectable,
  InjectionToken,
  NgZone,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';

/**
 * Turns on "the backend's message wins" for an app: a rejected save puts the API's own
 * message on the field it names, highlights that field and scrolls to it (inside the open
 * popup when there is one), and a component's generic "Failed to save" toast no longer
 * replaces the API's toast. Off by default so an app opts in from its app config.
 */
export const API_FIELD_ERRORS_ENABLED = new InjectionToken<boolean>('API_FIELD_ERRORS_ENABLED', {
  providedIn: 'root',
  factory: () => false,
});

/** The error key the reusables already render as text (`errors.server`). */
export const API_FIELD_ERROR_KEY = 'server';

export interface ApiFieldError {
  /** The field as the API named it (`Clients[0].DateOfBirth`), or '' when it named none. */
  readonly field: string;
  readonly message: string;
}

interface ApiFieldTarget {
  readonly host: HTMLElement;
  readonly controls: () => readonly (AbstractControl | null | undefined)[];
  readonly label?: () => string;
  readonly changeDetector?: ChangeDetectorRef;
}

interface Candidate {
  readonly el: HTMLElement;
  readonly control?: AbstractControl;
  readonly changeDetector?: ChangeDetectorRef;
  /** Normalised path keys, indices left out (`['clients', 'dateofbirth']`). */
  readonly keys: readonly string[];
  /** The path's array indices, joined (`'0'`), so `Clients[0]` can beat `Clients[1]`. */
  readonly indices: string;
  readonly label: string;
}

/** 4xx statuses that mean "this input is wrong". 401/403/404 and 5xx never point at a field. */
const FIELD_ERROR_STATUSES = new Set([400, 409, 422]);

const FIELD_ATTRIBUTE_SELECTOR =
  '[formcontrolname], [data-control], [data-field], input[name], select[name], textarea[name]';

/** Backend spellings that don't normalise onto the FE control name on their own. */
const KEY_SYNONYMS: Readonly<Record<string, string>> = {
  dob: 'dateofbirth',
  birthdate: 'dateofbirth',
  mobile: 'mobilenumber',
  phone: 'mobilenumber',
  phonenumber: 'mobilenumber',
  telephone: 'mobilenumber',
  postalcode: 'postcode',
  zipcode: 'postcode',
  emailaddress: 'email',
};

const DECORATION_CLASS = 'api-field-error';
const FLASH_CLASS = 'api-field-error-flash';
const MESSAGE_CLASS = 'api-field-error-message';
const FLASH_MS = 2400;
/** A component's own error toast for the same failed request lands within this window. */
const API_TOAST_PRIORITY_MS = 1000;
/** A validation toast this soon after a button press is that button's save being blocked. */
const LOCAL_TOAST_CLICK_MS = 1500;

/** A field the page has already turned red: invalid and touched, or a reusable's red state. */
const FLAGGED_FIELD_SELECTOR =
  '.ng-invalid.ng-touched, .ng-invalid.ng-dirty, [data-date-picker].ng-invalid, [aria-invalid="true"], ' +
  '[data-invalid="true"], .border-red500, .border-red600, .border-red500\\/60, .border-red400';
/** Block-level validation text (never the `*` asterisk spans), for hand-rolled fields. */
const FLAGGED_MESSAGE_SELECTOR =
  'p.text-red600, p.text-red500, p.text-red400, small.text-red600, small.text-red500, .field-error';

/**
 * Maps a rejected save's field errors onto the fields on screen. Form-control reusables
 * register themselves (see `registerApiFieldTarget`); anything else is found through its
 * `formControlName` / `name` / `data-control` attribute. Driven from `apiToastInterceptor`,
 * so no page has to wire anything up.
 */
@Injectable({ providedIn: 'root' })
export class ApiFieldErrorsService {
  readonly enabled = inject(API_FIELD_ERRORS_ENABLED);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly targets = new Set<ApiFieldTarget>();
  private readonly cleanups: Array<() => void> = [];
  private lastApiErrorAt = 0;
  private lastButtonClick: { at: number; el: HTMLElement } | null = null;

  constructor() {
    if (!this.enabled || !this.isBrowser) {
      return;
    }
    // Remembers the Save / Next / Add the user just pressed, so a validation toast it
    // raises can take them to the field. Capture phase, outside the zone: it only records.
    inject(NgZone).runOutsideAngular(() =>
      document.addEventListener(
        'click',
        (event) => {
          const el = (event.target as Element | null)?.closest<HTMLElement>('button, [role="button"]');
          if (el) {
            this.lastButtonClick = { at: Date.now(), el };
          }
        },
        true,
      ),
    );
  }

  register(target: ApiFieldTarget): () => void {
    if (!this.isBrowser) {
      return () => undefined;
    }
    this.targets.add(target);
    return () => this.targets.delete(target);
  }

  /** Called by the toast service: an API error toast was just shown. */
  noteApiErrorToast(): void {
    this.lastApiErrorAt = Date.now();
  }

  /** True right after an API error toast, while the failing request's own handler runs. */
  isWithinApiErrorWindow(): boolean {
    return this.enabled && Date.now() - this.lastApiErrorAt < API_TOAST_PRIORITY_MS;
  }

  /**
   * Called by the toast service for an error toast the page raised itself. When that
   * came straight from a button press ("Please complete…" from a Save that never called
   * the API), take the user to the first field flagged red — in the popup the button
   * sits in, or on the page. Deferred past a tick and two frames so a gate that marks
   * its fields touched just after the toast has had them painted red first.
   */
  noteLocalErrorToast(): void {
    const click = this.lastButtonClick;
    if (!this.enabled || !this.isBrowser || !click || Date.now() - click.at > LOCAL_TOAST_CLICK_MS) {
      return;
    }
    this.lastButtonClick = null;

    setTimeout(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const scope = (click.el.isConnected ? openOverlayOf(click.el) : null) ?? document.body;
          const target = findFirstFlaggedField(scope);
          if (!target) {
            return;
          }
          target.classList.add(FLASH_CLASS);
          setTimeout(() => target.classList.remove(FLASH_CLASS), FLASH_MS);
          this.scrollTo(target);
        }),
      ),
    );
  }

  /**
   * Scrolls to `el` and pulses it, for a blocked save whose missing answer is a whole
   * block rather than one control ("select at least one cover amount").
   */
  revealField(el: HTMLElement | null | undefined): void {
    if (!this.isBrowser || !el) {
      return;
    }
    el.classList.add(FLASH_CLASS);
    setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);
    this.scrollTo(el);
  }

  /**
   * Handles a rejected response body. Deferred one macrotask so the page's own error
   * handler (which may reset flags, re-render or re-patch the form) has run first and
   * can't wipe the error straight back off the control.
   */
  handleErrorResponse(body: unknown, status: number): void {
    if (!this.enabled || !this.isBrowser) {
      return;
    }
    if (status !== 200 && !FIELD_ERROR_STATUSES.has(status)) {
      return;
    }

    const errors = extractApiFieldErrors(body);
    if (errors.length === 0) {
      return;
    }

    setTimeout(() => this.apply(errors));
  }

  private apply(errors: readonly ApiFieldError[]): void {
    this.clearDecorations();

    const candidates = this.scopeToOpenOverlay(this.collectCandidates());
    if (candidates.length === 0) {
      return;
    }

    const applied: Array<{ candidate: Candidate; message: string }> = [];
    const seen = new Set<AbstractControl | HTMLElement>();
    for (const error of errors) {
      for (const candidate of this.matchCandidates(error, candidates)) {
        const key = candidate.control ?? candidate.el;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        applied.push({ candidate, message: error.message });
      }
    }

    if (applied.length === 0) {
      return;
    }

    for (const { candidate, message } of applied) {
      this.markControl(candidate, message);
    }

    // Two frames: Angular renders the reusable's own error text first, so the fallback
    // message below is only added where nothing on the field shows it yet.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        for (const { candidate, message } of applied) {
          this.decorate(candidate, message);
        }
        this.scrollTo(sortDocumentOrder(applied.map(({ candidate }) => candidate.el))[0]);
      }),
    );
  }

  private collectCandidates(): Candidate[] {
    const candidates: Candidate[] = [];
    const registeredHosts: HTMLElement[] = [];

    for (const target of this.targets) {
      if (!isVisible(target.host)) {
        continue;
      }
      registeredHosts.push(target.host);
      const label = normalizeKey(target.label?.() ?? '');
      for (const control of target.controls()) {
        if (!control || control.disabled) {
          continue;
        }
        const path = controlPath(control);
        if (path.length === 0 && !label) {
          continue;
        }
        candidates.push({
          el: target.host,
          control,
          changeDetector: target.changeDetector,
          keys: path.filter((part) => !isIndex(part)).map(normalizeKey),
          indices: path.filter(isIndex).join('.'),
          label,
        });
      }
    }

    // Fields not built on a registered reusable: raw inputs, CVA components bound with
    // formControlName (phone, checkbox, toggle…), hand-rolled yes/no groups with data-control.
    const elements = Array.from(document.querySelectorAll<HTMLElement>(FIELD_ATTRIBUTE_SELECTOR));
    for (const el of elements) {
      if (!isVisible(el) || registeredHosts.some((host) => host.contains(el))) {
        continue;
      }
      const path = attributePath(el);
      if (path.length === 0) {
        continue;
      }
      candidates.push({
        el,
        keys: path.filter((part) => !isIndex(part)).map(normalizeKey),
        indices: path.filter(isIndex).join('.'),
        label: normalizeKey(labelFor(el)),
      });
    }

    return candidates;
  }

  /** A save from a popup or side panel is about the fields in it, never the page behind. */
  private scopeToOpenOverlay(candidates: Candidate[]): Candidate[] {
    const inOverlay = candidates.filter((candidate) => !!openOverlayOf(candidate.el));
    return inOverlay.length > 0 ? inOverlay : candidates;
  }

  private matchCandidates(error: ApiFieldError, candidates: readonly Candidate[]): Candidate[] {
    const scored = error.field
      ? candidates.map((candidate) => ({ candidate, score: scoreByField(error.field, candidate) }))
      : candidates.map((candidate) => ({ candidate, score: scoreByMessage(error.message, candidate) }));

    const best = Math.max(0, ...scored.map(({ score }) => score));
    return best > 0 ? scored.filter(({ score }) => score === best).map(({ candidate }) => candidate) : [];
  }

  private markControl(candidate: Candidate, message: string): void {
    const control = candidate.control;
    if (!control) {
      return;
    }
    control.setErrors({ ...(control.errors ?? {}), [API_FIELD_ERROR_KEY]: message });
    control.markAsTouched();
    candidate.changeDetector?.markForCheck();

    // The server error goes as soon as the value changes (Angular re-runs the validators
    // and replaces `errors`); the decorations below have to be taken down by hand.
    const subscription = control.valueChanges.subscribe(() => {
      subscription.unsubscribe();
      this.undecorate(candidate.el);
    });
    this.cleanups.push(() => subscription.unsubscribe());
  }

  private decorate(candidate: Candidate, message: string): void {
    const el = candidate.el;
    el.classList.add(DECORATION_CLASS, FLASH_CLASS);
    const flashTimer = setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);

    const shown = normalizeText(el.textContent ?? '').includes(normalizeText(message));
    if (!shown) {
      const note = document.createElement('p');
      note.className = MESSAGE_CLASS;
      note.textContent = message;
      if (isNativeField(el)) {
        el.insertAdjacentElement('afterend', note);
      } else {
        el.appendChild(note);
      }
    }

    const onEdit = () => this.undecorate(el);
    el.addEventListener('input', onEdit, { once: true });
    el.addEventListener('change', onEdit, { once: true });
    this.cleanups.push(() => {
      clearTimeout(flashTimer);
      el.removeEventListener('input', onEdit);
      el.removeEventListener('change', onEdit);
      this.undecorate(el);
    });
  }

  private undecorate(el: HTMLElement): void {
    el.classList.remove(DECORATION_CLASS, FLASH_CLASS);
    const note = isNativeField(el) ? el.nextElementSibling : el.querySelector(`:scope > .${MESSAGE_CLASS}`);
    if (note?.classList.contains(MESSAGE_CLASS)) {
      note.remove();
    }
  }

  private clearDecorations(): void {
    for (const cleanup of this.cleanups.splice(0)) {
      cleanup();
    }
  }

  private scrollTo(el: HTMLElement | undefined): void {
    if (!el) {
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // After the smooth scroll settles, or focus() fights it with a second jump.
    setTimeout(() => {
      const focusable = isNativeField(el)
        ? el
        : el.querySelector<HTMLElement>('input:not([type=hidden]), textarea, select, button, [tabindex]:not([tabindex="-1"])');
      focusable?.focus({ preventScroll: true });
    }, 350);
  }
}

/**
 * Lets a form-control reusable be found by an API field error. Call from the
 * constructor (injection context); it unregisters itself on destroy.
 */
export function registerApiFieldTarget(
  controls: () => readonly (AbstractControl | null | undefined)[],
  label?: () => string,
): void {
  const service = inject(ApiFieldErrorsService);
  if (!service.enabled) {
    return;
  }
  const unregister = service.register({
    host: inject(ElementRef<HTMLElement>).nativeElement,
    controls,
    label,
    changeDetector: inject(ChangeDetectorRef),
  });
  inject(DestroyRef).onDestroy(unregister);
}

/**
 * Every (field, message) pair a rejected response carries. Reads the API envelope
 * (`{ error: { field?, message, details: [{ field, message }] } }`), ASP.NET's
 * `errors: { Field: ['…'] }` dictionary, and plain `errors: [{ field, message }]` lists.
 * The top-level message only counts when nothing more specific came back.
 */
export function extractApiFieldErrors(body: unknown): ApiFieldError[] {
  const found: ApiFieldError[] = [];
  const seen = new Set<string>();
  const add = (field: unknown, message: unknown): void => {
    const text = toMessage(message);
    if (!text) {
      return;
    }
    const name = typeof field === 'string' || typeof field === 'number' ? String(field).trim() : '';
    const key = `${name}\u0000${text}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    found.push({ field: name, message: text });
  };

  const visitContainer = (value: unknown, inheritedField = '', depth = 0): void => {
    if (depth > 5 || value === null || value === undefined) {
      return;
    }
    if (typeof value === 'string') {
      add(inheritedField, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => visitContainer(item, inheritedField, depth + 1));
      return;
    }
    if (!isRecord(value)) {
      return;
    }
    const field = value['field'] ?? value['fieldName'] ?? value['propertyName'] ?? value['property'] ?? value['path'] ?? inheritedField;
    const message = value['message'] ?? value['errorMessage'] ?? value['description'];
    if (message !== undefined) {
      add(field, message);
      return;
    }
    // A dictionary: { FirstName: ['First name is required'] }.
    for (const [key, nested] of Object.entries(value)) {
      visitContainer(nested, key, depth + 1);
    }
  };

  const roots = [body, isRecord(body) ? body['error'] : undefined].filter(isRecord);
  for (const root of roots) {
    visitContainer(root['details']);
    visitContainer(root['errors']);
    visitContainer(root['validationErrors']);
    if (root['field'] !== undefined) {
      add(root['field'], root['message']);
    }
  }

  if (found.length === 0) {
    for (const root of roots) {
      add('', root['message']);
    }
  }

  return found;
}

function scoreByField(field: string, candidate: Candidate): number {
  const parts = field.split(/[.[\]/$]+/).filter(Boolean);
  const keys = parts.filter((part) => !isIndex(part)).map(normalizeKey).filter(Boolean);
  const leaf = keys[keys.length - 1];
  if (!leaf) {
    return 0;
  }

  const candidateLeaf = candidate.keys[candidate.keys.length - 1];
  if (candidateLeaf && canonicalKey(candidateLeaf) === canonicalKey(leaf)) {
    let suffix = 1;
    while (
      suffix < keys.length &&
      suffix < candidate.keys.length &&
      canonicalKey(keys[keys.length - 1 - suffix]) === canonicalKey(candidate.keys[candidate.keys.length - 1 - suffix])
    ) {
      suffix++;
    }
    const indices = parts.filter(isIndex).join('.');
    return 10 + suffix * 2 + (candidateLeaf === leaf ? 1 : 0) + (indices && indices === candidate.indices ? 1 : 0);
  }

  if (candidate.label && canonicalKey(candidate.label) === canonicalKey(leaf)) {
    return 5;
  }

  return 0;
}

/** No field named: the message names it ("Date of birth must be in the past"). */
function scoreByMessage(message: string, candidate: Candidate): number {
  const text = normalizeKey(message);
  const leaf = candidate.keys[candidate.keys.length - 1] ?? '';
  return Math.max(
    candidate.label.length >= 5 && text.includes(candidate.label) ? candidate.label.length : 0,
    leaf.length >= 5 && text.includes(leaf) ? leaf.length : 0,
  );
}

function controlPath(control: AbstractControl): string[] {
  const path: string[] = [];
  let current: AbstractControl = control;
  while (current.parent) {
    const siblings = (current.parent as unknown as { controls: Record<string, AbstractControl> | AbstractControl[] }).controls;
    const key = Array.isArray(siblings)
      ? String(siblings.indexOf(current))
      : Object.keys(siblings).find((name) => siblings[name] === current);
    if (key === undefined || key === '-1') {
      break;
    }
    path.unshift(key);
    current = current.parent;
  }
  return path;
}

/** `formControlName="dob"` inside `formGroupName="client"` → `['client', 'dob']`. */
function attributePath(el: HTMLElement): string[] {
  const own =
    el.getAttribute('formcontrolname') ??
    el.getAttribute('data-control') ??
    el.getAttribute('data-field') ??
    el.getAttribute('name') ??
    '';
  if (!own.trim()) {
    return [];
  }
  const path = [own.trim()];
  let parent = el.parentElement?.closest<HTMLElement>('[formgroupname], [formarrayname]');
  while (parent) {
    const name = parent.getAttribute('formgroupname') ?? parent.getAttribute('formarrayname');
    if (name) {
      path.unshift(name);
    }
    parent = parent.parentElement?.closest<HTMLElement>('[formgroupname], [formarrayname]');
  }
  return path;
}

function labelFor(el: HTMLElement): string {
  const id = el.getAttribute('id');
  const byFor = id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
  return (byFor?.textContent ?? el.getAttribute('aria-label') ?? '').replace(/\*/g, '').trim();
}

/** The open popup / side panel holding `el`, if any. */
function openOverlayOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const isDialog = node.getAttribute('role') === 'dialog' || node.getAttribute('aria-modal') === 'true';
    // Popups are `fixed inset-0`. Short fixed bars (the top bar's search) are not overlays.
    const isFixedLayer = !isDialog && getComputedStyle(node).position === 'fixed';
    if (!isDialog && !isFixedLayer) {
      continue;
    }
    const rect = node.getBoundingClientRect();
    const onScreen = rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
    if (onScreen && (isDialog || rect.height >= window.innerHeight * 0.6)) {
      return node;
    }
  }
  return null;
}

/**
 * The first field inside `scope` the page flagged red, in document order. Leaves only:
 * a form or group wrapper is flagged too, and scrolling to it lands above the field.
 */
function findFirstFlaggedField(scope: HTMLElement): HTMLElement | null {
  const flagged = Array.from(scope.querySelectorAll<HTMLElement>(FLAGGED_FIELD_SELECTOR)).filter(isVisible);
  const leaves = flagged.filter((el) => el.tagName !== 'FORM' && !el.querySelector(FLAGGED_FIELD_SELECTOR));
  if (leaves.length > 0) {
    return sortDocumentOrder(leaves)[0];
  }

  const message = sortDocumentOrder(
    Array.from(scope.querySelectorAll<HTMLElement>(FLAGGED_MESSAGE_SELECTOR)).filter(
      (el) => isVisible(el) && (el.textContent ?? '').trim().length > 0,
    ),
  )[0];
  return message ? ((message.closest('[data-field]') as HTMLElement | null) ?? message.parentElement ?? message) : null;
}

function canonicalKey(key: string): string {
  const synonym = KEY_SYNONYMS[key] ?? key;
  // `branch` / `branchId` / `branchIds` all name the same picker.
  return synonym.length > 4 ? synonym.replace(/ids?$/, '') : synonym;
}

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function isIndex(part: string): boolean {
  return /^\d+$/.test(part);
}

function isVisible(el: HTMLElement): boolean {
  return el.isConnected && el.getClientRects().length > 0;
}

function isNativeField(el: HTMLElement): boolean {
  return ['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName);
}

function toMessage(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    return value.map(toMessage).filter(Boolean).join('\n');
  }
  return isRecord(value) ? toMessage(value['message'] ?? value['errorMessage']) : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function sortDocumentOrder(elements: HTMLElement[]): HTMLElement[] {
  return [...elements].sort((a, b) => {
    const relation = a.compareDocumentPosition(b);
    if (relation & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (relation & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}
